import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSessionStore } from "@/store/session";
import en from "@/lang/en.json";
import de from "@/lang/de.json";

export const LoginPage = () => {
	const navigate = useNavigate();
	const { login, verifyMfa, cancelMfa, challenge } = useSessionStore();
	const [language, setLanguage] = useState(
		navigator.language.startsWith("de") ? "de" : "en",
	);
	const messages = language === "de" ? de : en;
	const t = (key: keyof typeof en) => messages[key];
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [code, setCode] = useState("");
	const [recovery, setRecovery] = useState(false);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [expired, setExpired] = useState(false);
	useEffect(() => {
		setExpired(false);
		if (!challenge) return;
		const timer = setTimeout(
			() => setExpired(true),
			Math.max(0, Date.parse(challenge.expiresAt) - Date.now()),
		);
		return () => clearTimeout(timer);
	}, [challenge]);
	return (
		<div className="flex min-h-screen items-center justify-center bg-muted-background px-4 py-12">
			<Card className="w-full max-w-md border border-border shadow-lg">
				<CardHeader className="space-y-2">
					<label htmlFor="audit-language">Language / Sprache</label>
					<select
						id="audit-language"
						value={language}
						onChange={(event) => setLanguage(event.target.value)}
					>
						<option value="en">English</option>
						<option value="de">Deutsch</option>
					</select>
					<CardTitle>{t(challenge ? "mfa.title" : "login")}</CardTitle>
					<CardDescription>
						{t(challenge ? "mfa.challengeHelp" : "description")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<form
						data-clarity-mask="true"
						data-ph-no-capture
						className="sentry-block space-y-5"
						onSubmit={async (event) => {
							event.preventDefault();
							if (loading || expired) return;
							setLoading(true);
							setError(null);
							try {
								if (challenge) {
									await verifyMfa(code, recovery, language);
									setCode("");
									navigate("/logs", { replace: true });
								} else {
									const authenticated = await login(
										{ email, password },
										language,
									);
									setPassword("");
									if (authenticated) navigate("/logs", { replace: true });
								}
							} catch (failure) {
								setError(
									failure instanceof Error && !(failure instanceof TypeError)
										? failure.message
										: t("mfa.unavailable"),
								);
							} finally {
								setLoading(false);
							}
						}}
					>
						{challenge ? (
							<div className="space-y-2">
								<label htmlFor="audit-code">
									{t(recovery ? "mfa.recoveryCode" : "mfa.code")}
								</label>
								<Input
									id="audit-code"
									value={code}
									onChange={(event) => setCode(event.target.value)}
									autoComplete="one-time-code"
									inputMode={recovery ? "text" : "numeric"}
									pattern={recovery ? undefined : "[0-9]{6}"}
									maxLength={recovery ? 64 : 6}
									required
									disabled={loading || expired}
									aria-describedby="audit-feedback"
									autoFocus
								/>
							</div>
						) : (
							<>
								<div className="space-y-2">
									<label htmlFor="email">{t("email")}</label>
									<Input
										id="email"
										type="email"
										autoComplete="email"
										required
										value={email}
										disabled={loading}
										onChange={(event) => setEmail(event.target.value)}
									/>
								</div>
								<div className="space-y-2">
									<label htmlFor="password">{t("password")}</label>
									<Input
										id="password"
										type="password"
										autoComplete="current-password"
										required
										minLength={8}
										value={password}
										disabled={loading}
										onChange={(event) => setPassword(event.target.value)}
									/>
								</div>
							</>
						)}
						<div
							id="audit-feedback"
							role={error || expired ? "alert" : undefined}
							className="text-destructive"
						>
							{expired ? t("mfa.expired") : error}
						</div>
						<Button
							type="submit"
							className="w-full"
							disabled={loading || expired}
						>
							{t(loading ? "mfa.working" : challenge ? "mfa.verify" : "login")}
						</Button>
						{challenge && (
							<>
								<Button
									type="button"
									variant="outline"
									disabled={loading}
									onClick={() => {
										setRecovery(!recovery);
										setCode("");
										setError(null);
									}}
								>
									{t(recovery ? "mfa.useAuthenticator" : "mfa.useRecovery")}
								</Button>
								<Button
									type="button"
									variant="ghost"
									disabled={loading}
									onClick={() => {
										cancelMfa();
										setRecovery(false);
										setCode("");
										setError(null);
									}}
								>
									{t("mfa.restart")}
								</Button>
							</>
						)}
					</form>
				</CardContent>
			</Card>
		</div>
	);
};
